// frontend/src/pages/AutoTest.js
import { useState, useEffect, useRef } from "react";
import api from "../api/client";
import Sidebar from "../components/Sidebar";

const ORANGE = "#D95B2A";
const ORANGE_LIGHT = "#F4EAE4";

export default function AutoTest() {
  const [step, setStep] = useState("home");
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [weakPoints, setWeakPoints] = useState([]);
  const [targetedThemes, setTargetedThemes] = useState([]);
  const [hasHistory, setHasHistory] = useState(false);
  const [nbQuestions, setNbQuestions] = useState(10);
  const [expandedSession, setExpandedSession] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    api.get("/api/autotest/history/")
      .then((res) => setHistory(res.data.history))
      .catch(() => {});
    api.get("/api/autotest/weakpoints/")
      .then((res) => {
        setWeakPoints(res.data.weak_sous_themes);
        setHasHistory(res.data.has_history);
      })
      .catch(() => {});
  }, []);

  const startTest = async () => {
    setStep("loading");
    try {
      const res = await api.get(`/api/autotest/questions/?n=${nbQuestions}`);
      setQuestions(res.data.questions);
      setTargetedThemes(res.data.targeted_sous_themes || []);
      setAnswers([]);
      setCurrent(0);
      setSelected(null);
      setConfirmed(false);
      setStep("quiz");
    } catch {
      setStep("home");
      alert("Impossible de charger les questions. Veuillez réessayer.");
    }
  };

  const confirm = () => {
    if (!selected) return;
    const q = questions[current];
    setAnswers((prev) => [
      ...prev,
      { term_id: q.id, term: q.term, selected, correct: q.correct_answer },
    ]);
    setConfirmed(true);
  };

  const next = () => {
    if (current + 1 < questions.length) {
      setCurrent((c) => c + 1);
      setSelected(null);
      setConfirmed(false);
    } else {
      submitTest();
    }
  };

  const submitTest = async () => {
    try {
      const res = await api.post("/api/autotest/submit/", { answers });
      setResult(res.data);
      if (res.data.session_id) setSessionId(res.data.session_id);
      setTimeout(() => { resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 200);
      setStep("result");
      api.get("/api/autotest/history/").then((r) => setHistory(r.data.history));
      api.get("/api/autotest/weakpoints/").then((r) => setWeakPoints(r.data.weak_sous_themes));
    } catch {
      alert("Erreur lors de l'envoi des résultats.");
    }
  };

  const restart = () => {
    setStep("home");
    setResult(null);
  };

  return (
    <div className="page-container">
      <Sidebar />
      <div className="content-container">
        <h1 style={{ fontWeight: 700, fontSize: "1.6rem", marginBottom: "2rem" }}>
          AutoTest
        </h1>

        {/* ── Accueil ── */}
        {step === "home" && (
          <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>

            {/* Carte démarrer */}
            <div style={cardStyle}>
              <h2 style={{ color: ORANGE, marginBottom: "0.5rem" }}>
                Les mots à retravailler
              </h2>
              <p style={{ color: "#555", marginBottom: "1rem", lineHeight: 1.6 }}>
                {hasHistory
                  ? "Le système analyse les performances et propose les notions à approfondir."
                  : "Jouez d'abord quelques parties pour que le système analyse vos points faibles."}
              </p>

              {/* Points faibles */}
              {weakPoints.length > 0 && (
                <div style={{ marginBottom: "1.5rem" }}>
                  <p style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.5rem", color: "#555" }}>
                    Les sous-thèmes à retravailler :
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                    {weakPoints.map((w, i) => (
                      <span key={i} style={{
                        background: ORANGE_LIGHT,
                        color: ORANGE,
                        borderRadius: 20,
                        padding: "0.2rem 0.7rem",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      }}>
                        {w.sous_theme} — {w.error_rate}% d'erreurs
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <label style={{ fontWeight: 600, marginBottom: "0.5rem", display: "block" }}>
                Nombre de questions
              </label>
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
                {[5, 10, 15, 20].map((n) => (
                  <button
                    key={n}
                    onClick={() => setNbQuestions(n)}
                    style={{
                      padding: "0.4rem 1rem",
                      borderRadius: 8,
                      border: `2px solid ${nbQuestions === n ? ORANGE : "#ddd"}`,
                      background: nbQuestions === n ? ORANGE : "#fff",
                      color: nbQuestions === n ? "#fff" : "#333",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <button onClick={startTest} style={btnPrimaryStyle}>
                Commencer →
              </button>
            </div>

            {/* Historique avec détail des erreurs */}
            {history.length > 0 && (
              <div style={{ ...cardStyle, flex: "1 1 280px" }}>
                <h2 style={{ marginBottom: "1rem", fontSize: "1rem", fontWeight: 700 }}>
                  Historique récent
                </h2>
                {history.map((h, i) => (
                  <div key={i} style={{ borderBottom: "1px solid #f0f0f0", marginBottom: "0.5rem" }}>
                    <div
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0", cursor: "pointer" }}
                      onClick={() => setExpandedSession(expandedSession === i ? null : i)}
                    >
                      <span style={{ fontSize: "0.85rem", color: "#555" }}>{h.date}</span>
                      <span style={{ fontWeight: 600 }}>{h.score}/{h.total}</span>
                      <span style={{
                        color: h.percentage >= 75 ? "#2e7d32" : h.percentage >= 50 ? ORANGE : "#c62828",
                        fontWeight: 600,
                      }}>
                        {h.percentage}%
                      </span>
                      <span style={{ color: "#aaa", fontSize: "0.8rem" }}>
                        {expandedSession === i ? "▲" : "▼"}
                      </span>
                    </div>

                    {/* Détail des erreurs */}
                    {expandedSession === i && h.wrong_answers && h.wrong_answers.length > 0 && (
                      <div style={{ background: "#fff8f6", borderRadius: 8, padding: "0.8rem", marginBottom: "0.5rem" }}>
                        <p style={{ fontWeight: 600, fontSize: "0.8rem", color: "#c62828", marginBottom: "0.5rem" }}>
                          Mots à retravailler :
                        </p>
                        {h.wrong_answers.map((w, j) => (
                          <div key={j} style={{ fontSize: "0.82rem", marginBottom: "0.3rem", color: "#444" }}>
                            <strong>{w.term}</strong> — Bonne réponse : <span style={{ color: "#2e7d32" }}>{w.correct}</span> · Réponse donnée : <span style={{ color: "#c62828" }}>{w.selected}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {expandedSession === i && (!h.wrong_answers || h.wrong_answers.length === 0) && (
                      <p style={{ fontSize: "0.82rem", color: "#2e7d32", padding: "0.5rem 0" }}>
                        ✓ Aucune erreur dans cette session !
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Chargement ── */}
        {step === "loading" && (
          <div style={{ textAlign: "center", marginTop: "5rem" }}>
            <div style={spinnerStyle} />
            <p style={{ color: "#888", marginTop: "1rem" }}>Analyse des points faibles…</p>
          </div>
        )}

        {/* ── Quiz ── */}
        {step === "quiz" && questions.length > 0 && (
          <div style={{ maxWidth: 640 }}>
            {targetedThemes.length > 0 && (
              <div style={{ marginBottom: "1rem", display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.8rem", color: "#888" }}>Focus sur :</span>
                {targetedThemes.map((t, i) => (
                  <span key={i} style={{ background: ORANGE_LIGHT, color: ORANGE, borderRadius: 20, padding: "0.1rem 0.6rem", fontSize: "0.8rem", fontWeight: 600 }}>
                    {t}
                  </span>
                ))}
              </div>
            )}

            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
                <span style={{ fontWeight: 600, color: ORANGE }}>
                  Question {current + 1} / {questions.length}
                </span>
                <span style={{ color: "#888", fontSize: "0.85rem" }}>
                  {questions[current].theme} › {questions[current].sous_theme}
                </span>
              </div>
              <div style={{ background: "#e0e0e0", borderRadius: 99, height: 6 }}>
                <div style={{
                  width: `${((current + 1) / questions.length) * 100}%`,
                  background: ORANGE, height: 6, borderRadius: 99, transition: "width 0.3s",
                }} />
              </div>
            </div>

            <div style={cardStyle}>
              <p style={{ color: "#888", fontSize: "0.85rem", marginBottom: "0.3rem" }}>
                Quelle est la traduction de :
              </p>
              <h2 style={{ fontSize: "1.8rem", fontWeight: 700, marginBottom: "0.5rem" }}>
                {questions[current].term}
              </h2>
              {questions[current].example_sentence && (
                <p style={{ color: "#666", fontSize: "0.9rem", fontStyle: "italic", marginBottom: "1.5rem", lineHeight: 1.5 }}>
                  "{questions[current].example_sentence}"
                </p>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {questions[current].choices.map((choice, i) => {
                  let bg = "#fff", border = "2px solid #e0e0e0", color = "#333";
                  if (confirmed) {
                    if (choice === questions[current].correct_answer) { bg = "#e8f5e9"; border = "2px solid #4caf50"; color = "#2e7d32"; }
                    else if (choice === selected) { bg = "#fdecea"; border = "2px solid #f44336"; color = "#c62828"; }
                  } else if (choice === selected) {
                    bg = ORANGE_LIGHT; border = `2px solid ${ORANGE}`;
                  }
                  return (
                    <button key={i} onClick={() => !confirmed && setSelected(choice)} style={{
                      padding: "0.8rem 1rem", borderRadius: 10, border, background: bg, color,
                      textAlign: "left", cursor: confirmed ? "default" : "pointer",
                      fontWeight: choice === selected ? 600 : 400, fontSize: "0.95rem", transition: "all 0.15s",
                    }}>
                      {choice}
                    </button>
                  );
                })}
              </div>

              <div style={{ marginTop: "1.5rem" }}>
                {!confirmed ? (
                  <button onClick={confirm} disabled={!selected} style={{ ...btnPrimaryStyle, opacity: selected ? 1 : 0.4 }}>
                    Valider
                  </button>
                ) : (
                  <button onClick={next} style={btnPrimaryStyle}>
                    {current + 1 < questions.length ? "Question suivante →" : "Voir les résultats →"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Résultats ── */}
        {step === "result" && result && (
          <div ref={resultRef} style={{ maxWidth: 680, width: "100%" }}>

            {/* Carte score */}
            <div style={{ ...cardStyle, textAlign: "center", marginBottom: "1.5rem", padding: "2rem" }}>
              {/* Cercle progress */}
              <div style={{ position: "relative", width: 120, height: 120, margin: "0 auto 1.2rem" }}>
                <svg width="120" height="120" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#f0e8e4" strokeWidth="10"/>
                  <circle
                    cx="60" cy="60" r="50" fill="none"
                    stroke={ORANGE} strokeWidth="10"
                    strokeDasharray={`${2 * Math.PI * 50}`}
                    strokeDashoffset={`${2 * Math.PI * 50 * (1 - result.percentage / 100)}`}
                    strokeLinecap="round"
                    transform="rotate(-90 60 60)"
                    style={{ transition: "stroke-dashoffset 0.8s ease" }}
                  />
                </svg>
                <div style={{
                  position: "absolute", top: "50%", left: "50%",
                  transform: "translate(-50%,-50%)",
                  fontSize: "1.6rem", fontWeight: 800, color: ORANGE,
                }}>
                  {result.percentage}%
                </div>
              </div>
              <h2 style={{ fontSize: "1.3rem", fontWeight: 700, marginBottom: "0.3rem" }}>
                {result.score} / {result.total} bonnes réponses
              </h2>
              <p style={{ color: "#888", marginBottom: "1.5rem", fontSize: "0.95rem" }}>
                {result.percentage >= 80 ? "🎉 Excellent travail !" : result.percentage >= 60 ? "👍 Bien joué, continue !" : "💪 Continue à pratiquer !"}
              </p>
              <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
                <button onClick={restart} style={btnPrimaryStyle}>
                  Recommencer
                </button>
                <button
                  onClick={async () => {
                    const url = sessionId
                      ? `/api/autotest/export-pdf/?session_id=${sessionId}`
                      : `/api/autotest/export-pdf/`;
                    const doFetch = (tok) => fetch(url, {
                      headers: { Authorization: `Bearer ${tok}` }
                    });
                    let token = localStorage.getItem('access') || localStorage.getItem('accessToken') || '';
                    let res = await doFetch(token);
                    if (res.status === 401) {
                      const refresh = localStorage.getItem('refreshToken') || localStorage.getItem('refresh') || '';
                      if (refresh) {
                        const r = await fetch('/api/auth/token/refresh/', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ refresh }),
                        });
                        if (r.ok) {
                          const tokenData = await r.json();
                          token = tokenData.access;
                          localStorage.setItem('access', token);
                          localStorage.setItem('accessToken', token);
                          res = await doFetch(token);
                        }
                      }
                    }
                    if (res.ok) {
                      const blob = await res.blob();
                      const link = document.createElement('a');
                      link.href = URL.createObjectURL(blob);
                      link.download = 'polyglow_revision.pdf';
                      link.click();
                    } else {
                      alert('Erreur lors de la génération du PDF.');
                    }
                  }}
                  style={{
                    ...btnPrimaryStyle,
                    background: "#fff",
                    color: ORANGE,
                    border: `2px solid ${ORANGE}`,
                    display: "flex", alignItems: "center", gap: "0.4rem",
                  }}
                >
                   Télécharger le livret
                </button>
              </div>
            </div>

            {/* Mots maîtrisés */}
            {result.correct_answers && result.correct_answers.length > 0 && (
              <div style={{ ...cardStyle, marginBottom: "1.2rem", border: "1.5px solid #e8f5e9" }}>
                <h3 style={{ fontWeight: 700, marginBottom: "1rem", color: "#2e7d32", fontSize: "1rem" }}>
                  Mots maîtrisés ({result.correct_answers.length})
                </h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {result.correct_answers.map((w, i) => (
                    <span key={i} style={{
                      background: "#e8f5e9", color: "#2e7d32", borderRadius: 20,
                      padding: "0.3rem 0.8rem", fontSize: "0.85rem", fontWeight: 600,
                      border: "1px solid #c8e6c9", display: "flex", alignItems: "center", gap: "0.3rem"
                    }}>
                      {w.term || w.correct} <span style={{ fontSize: "0.75rem" }}>✓</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Mots à retravailler — tableau */}
            {result.wrong_answers && result.wrong_answers.length > 0 && (
              <div style={{ ...cardStyle, border: "1.5px solid #fce4e4" }}>
                <h3 style={{ fontWeight: 700, marginBottom: "1rem", color: "#c62828", fontSize: "1rem" }}>
                  Mots à retravailler ({result.wrong_answers.length})
                </h3>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                  <thead>
                    <tr style={{ background: "#fafafa", borderBottom: "2px solid #f0f0f0" }}>
                      <th style={{ ...thStyle, textAlign: "left", width: "35%" }}>Mot</th>
                      <th style={{ ...thStyle, textAlign: "left", width: "35%" }}>Votre réponse</th>
                      <th style={{ ...thStyle, textAlign: "left", width: "30%" }}>Bonne réponse</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.wrong_answers.map((w, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f5f5f5", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>{w.term}</td>
                        <td style={tdStyle}>
                          <span style={{
                            background: "#fdecea", color: "#c62828",
                            borderRadius: 12, padding: "0.15rem 0.6rem",
                            fontSize: "0.82rem", fontWeight: 500,
                            display: "inline-flex", alignItems: "center", gap: "0.3rem"
                          }}>
                            ✕ {w.selected}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            background: "#e8f5e9", color: "#2e7d32",
                            borderRadius: 12, padding: "0.15rem 0.6rem",
                            fontSize: "0.82rem", fontWeight: 500,
                            display: "inline-flex", alignItems: "center", gap: "0.3rem"
                          }}>
                            ✓ {w.correct}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const cardStyle = {
  background: "#fff", borderRadius: 16, padding: "1.5rem",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)", flex: "1 1 320px",
};
const btnPrimaryStyle = {
  background: "#D95B2A", color: "#fff", border: "none", borderRadius: 10,
  padding: "0.75rem 1.5rem", fontWeight: 700, fontSize: "0.95rem", cursor: "pointer",
};
const thStyle = { padding: "0.4rem 0.5rem", fontWeight: 600, fontSize: "0.8rem" };
const tdStyle = { padding: "0.5rem" };
const spinnerStyle = {
  width: 40, height: 40, border: "4px solid #eee",
  borderTop: "4px solid #D95B2A", borderRadius: "50%", margin: "0 auto",
};